import { Injectable } from '@angular/core';
import * as _ from "lodash";
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ReactiveForm } from 'src/app/core/resources/reactive-form';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Endpoints } from 'src/app/core/resources/endpoints';
import { Utils } from 'src/app/core/resources/utils';
import { Cargo, Cargo as CargoInterface, CargoRequest } from "src/app/core/interfaces/cargo";
import { map } from 'rxjs/operators';
import { ServiceResources } from '../resources/services';
import { Tag } from 'src/app/core/interfaces/tag';
import { Destination } from 'src/app/core/interfaces/destination';
import { AddressCargo } from 'src/app/core/interfaces/addressCargo';
import { DateManager } from 'src/app/core/managers/date.manager';
import { TRIP_TYPES } from 'src/app/core/enums/tripTypes.enum';
import { Patterns } from 'src/app/core/resources/patterns';
import { CargoDetailService } from '../../cargo/cargo-detail/cargo-detail.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { PermissionRole } from 'src/app/core/resources/permission-role';
import { Permission } from 'src/app/core/resources/permission';
import { DateFormatPipe } from 'src/app/core/pipe/dateFormat.pipe';
import { Address } from 'src/app/core/interfaces/address';
import { CargoMessages } from 'src/app/core/messages/cargo-messages.enum';
import { AmountsCargoEnum } from 'src/app/core/enums/amountsCargo.enum';
import { CompanyManager } from 'src/app/core/managers/company.manager';
import { Fmt } from 'src/app/core/messages/fmt';
import { FormMessages } from 'src/app/core/messages/form-messages.enum';
import { CargoManager } from 'src/app/core/managers/cargo.manager';
import { ConsignmentCargo } from 'src/app/core/interfaces/consignmentCargo';
import { extractCargoFromRndcResponse, mergeCargoFromRndcResponse } from 'src/app/modules/rndc/rndc-utils';
import { ThirdParty } from 'src/app/core/interfaces/thirdParty';
import { AuthService } from 'src/app/core/services/authentication.service';
import { Global } from 'src/app/core/resources/global';
import { GoogleService } from 'src/app/core/services/google.service';
import { VehiclesService } from '../../administration/vehicles/list-vehicles.service';
import { Vehicle } from 'src/app/core/interfaces/vehicle';
import { ConsignmentManager } from 'src/app/core/managers/consignment.manager';
import { SettingId } from 'src/app/core/interfaces/saas-config';
import { ServiceLock } from 'src/app/core/interfaces/satelliteLock';
import { CargoStateEnum } from 'src/app/core/enums/cargoState.enum';
import { VehicleAssignManager } from 'src/app/core/managers/vehicle-assign.manager';
import { User } from 'src/app/core/interfaces/user';
import { vehicleAxles } from 'src/app/core/resources/vehicleAxles';
import {
    ChangeTransportCompanyRequest,
    ChangeTransportCompanyResponse,
    TransportCompanyChangeJob
} from 'src/app/core/interfaces/transport-company-change';
import {
    DOCUMENT_RECREATION_IN_PROGRESS_MESSAGE,
    isDocumentRecreationInProgress
} from 'src/app/core/resources/transport-company-change.utils';

@Injectable({
    providedIn: 'root'
})
export class MenuServiceService extends ReactiveForm {
    public cargo: Cargo;
    public cargoConsignments: ConsignmentCargo[] = [];
    public cargoVehicle: Vehicle;
    formObservable: BehaviorSubject<Cargo> = new BehaviorSubject<Cargo>(this.form.value);
    consignmentsObservable: BehaviorSubject<ConsignmentCargo[]> = new BehaviorSubject<ConsignmentCargo[]>([]);
    isListeningChangesFromFirebase: boolean = false;
    permission = Permission;
    readonly regimeTypes = {
        "Exportación": ["DUTA", "DTAI", "Nacionalizado"],
        "Importación": ["OTM", "DUTA", "Nacionalizado"]
    };
    readonly serviceTypes = [
        {
            name: "Carga suelta",
            types: ["Carga expresa", "Carga consolidada"],
            image: "/assets/svg/icons/icon-load-free.svg"
        },
        {
            name: "Contenedor",
            types: ["Contenedor vacío express", "Contenedor lleno express", "Contenedor vacío con carga de compensación", "Contenedor lleno consolidado"],
            containerSizes: ['Un contenedor de 45 pies', 'Un contenedor de 40 pies', 'Dos contenedores de 20 pies', 'Un contenedor de 20 pies'],
            image: "/assets/svg/icons/icon-container.svg"
        }
    ];
    readonly regimeTypesWithDateVesselArrival = ['OTM', 'DTA', 'DUTA'];
    readonly consignmentTypes = [
        { code: 'G', description: "General", tooltip: "Viaje estandar de origen a destino." },
        { code: 'P', description: "Mercancía consolidada", tooltip: "En una misma remesa se agrupa mercancía de varios generadores de carga. En este caso el contratante en el campo generador de carga se puede colocar en la misma empresa de transporte." },
        { code: 'C', description: "Contenedor cargado", tooltip: "Se utiliza cuando se transporta para el mismo cliente el contenedor y la mercancía que va en él." },
        { code: 'V', description: "Contenedor vacío.", tooltip: "Se utiliza cuando se transporta un contenedor vacío o cuando el contenedor vacío se debe facturar en una factura diferente a la factura del transporte de la mercancía." },
    ];
    readonly cargoNatureTypes = [
        { code: '1', description: "Carga General (Carga Normal)" },
        { code: '2', description: "Mercancía Peligrosa" },
        { code: '3', description: "Carga Extradimensionada" },
        { code: '4', description: "Carga Extrapesada" },
        { code: '5', description: "Mercancía Peligrosa: Residuos Peligrosos" },
        { code: '6', description: "Semovientes" },
        { code: '7', description: "Refrigerada" },
    ];
    readonly operationTypes = [
        { code: '1', description: "Importación", tooltip: "Viaje desde un puerto hacia cualquier ciudad" },
        { code: '2', description: "Exportación", tooltip: "Viaje desde cualquier ciudad hacia puerto" },
        { code: '3', description: "Paqueteo urbano", tooltip: "Paqueteo dentro del mismo municipio." },
        { code: '4', description: "Paqueteo nacional", tooltip: "Paqueteo con municipio de origen distinto al municipio de destino" },
        { code: '5', description: "Masivo nacional", tooltip: "Viaje masivo con municipio de origen igual al municipio de destino" },
        { code: '6', description: "Viaje multiparada", tooltip: "Viaje nacional que tiene paradas en municipios intermedios distintos entre el origen y el destino con al menos dos remesas." },
        { code: '7', description: "Viaje ida y retorno", tooltip: "Operación con dos trayectos ida y vuelta. Entre dos municipios distintos y con al menos dos remesas." },
        { code: '8', description: "Masivo urbano", tooltip: "Viaje masivo con municipio de origen distinto al municipio de destino" },
        { code: '9', description: "Varios viajes en el día", tooltip: "Operación que cubre varios viajes en un mismo día para un mismo vehículo entre un único origen y un único destino" },
        { code: '10', description: "Viaje vacío", tooltip: "Viaje vacío" },
        { code: '11', description: "Viaje de contenedor vacío", tooltip: "Viaje de sólo contenedor vacío." }
    ];
    readonly urbanManifestTypes = ['U', 'D'];
    readonly nationalManifestTypes = ['G', 'M', 'I', 'D'];
    readonly timePactOptions = this.getTimePactList();
    readonly consignmentFieldsToCheck = this.global.consignmentFieldsToCheck;

    constructor(
        private http: HttpClient,
        private endpointResources: Endpoints,
        private utils: Utils,
        public formBuilder: FormBuilder,
        private global: Global,
        private cargoDetailService: CargoDetailService,
        private authService: AuthService,
        private serviceResources: ServiceResources,
        private patterns: Patterns,
        private spinner: NgxSpinnerService,
        private companyManager: CompanyManager,
        private permissionRole: PermissionRole,
        private cargoManager: CargoManager,
        private googleService: GoogleService,
        private vehiclesService: VehiclesService,
        private consignmentManager: ConsignmentManager,
        private vehicleAssignManager: VehicleAssignManager
    ) {
        super(
            formBuilder,
            utils.clone(serviceResources.cargoMock),
            utils.clone(serviceResources.cargoMock)
        );
        this.ensureTransportCompanyControl();
        this.ensureContainersControl();
    }

    public resetForm() {
        this.form = new ReactiveForm(this.formBuilder, this.utils.clone(this.serviceResources.cargoMock)).form;
        this.ensureTransportCompanyControl();
        this.ensureContainersControl();
    }

    /**
     * La fábrica de formularios histórica crea un FormGroup vacío para valores
     * null. transportCompany almacena un objeto completo, por lo que debe ser
     * siempre un FormControl para poder asignarlo desde la información del viaje.
     */
    private ensureTransportCompanyControl(): void {
        this.form.setControl('transportCompany', new FormControl(null));
    }


    private ensureContainersControl(): void {
        const currentValue = this.form.get('containers') ? this.form.get('containers').value : [];
        const containers = Array.isArray(currentValue) ? currentValue : [];
        this.form.setControl('containers', new FormControl(containers));
    }

    public patchForm(newCargo: Cargo, isListeningChangesFromFirebase: boolean = false) {
        this.isListeningChangesFromFirebase = isListeningChangesFromFirebase;
        this.resetForm();
        const originAddresses: AddressCargo[] = this.utils.getNestedValue(newCargo, 'cargoFeature.uploadDownload.origin.addresses');
        if (originAddresses && originAddresses.length)
            originAddresses.forEach((address: AddressCargo, i: number) => i && this.form.get('cargoFeature.uploadDownload.origin.addresses').push(this.createOriginAddressGroup()));

        const destinationCities: Destination[] = this.utils.getNestedValue(newCargo, 'cargoFeature.uploadDownload.destination');
        if (destinationCities && destinationCities.length)
            destinationCities.forEach((destinationCity: Destination, i: number) => {
                if (i) this.form.get('cargoFeature.uploadDownload.destination').push(this.createDestinationGroup());
                const addresses: AddressCargo[] = this.utils.getNestedValue(destinationCity, 'addresses');
                if (addresses && addresses.length)
                    addresses.forEach((address: AddressCargo, j: number) => {
                        if (j) this.form.get('cargoFeature.uploadDownload.destination').at(i).get('addresses').push(this.createDestinationAddressGroup());
                        const guidesRecipients: Address[] = this.utils.getNestedValue(address, 'guidesRecipients');
                        if (guidesRecipients && guidesRecipients.length)
                            guidesRecipients.forEach(() => this.form.get('cargoFeature.uploadDownload.destination').at(i).get('addresses').at(j).get('guidesRecipients').push(this.createGuideRecipientGroup()));
                        const consignments: string[] = this.utils.getNestedValue(address, 'consignments');
                        if (consignments && consignments.length)
                            consignments.forEach(() => this.form.get('cargoFeature.uploadDownload.destination').at(i).get('addresses').at(j).get('consignments').push(new FormControl('')));
                        this.ensureAddressContainersControl(this.form.get('cargoFeature.uploadDownload.destination').at(i).get('addresses').at(j) as FormGroup);
                    });
            });
        this.setUnexistingElements(newCargo);
        this.ensureContainersControl();
        this.form.patchValue(newCargo);
        this.formObservable.next(this.form.value);
    }

    private setUnexistingElements(newCargo: any, currentGroup: FormGroup = this.form) {
        const keys = Object.keys(newCargo);
        let elementsNotExisting = [];
        keys.forEach(key => {
            const cargoValue = this.utils.getNestedValue(newCargo, key);
            const formValue = this.utils.getNestedValue(currentGroup.value, key);
            if (cargoValue !== undefined && formValue === undefined) {
                elementsNotExisting.push(key);
                if (Array.isArray(newCargo[key])) {
                    const arr = this.formBuilder.array(newCargo[key]);
                    if (currentGroup) currentGroup.addControl(key, arr);
                    else this.form.addControl(key, arr);
                } else if (typeof newCargo[key] === 'object' && newCargo[key] !== null) {
                    const form = this.utils.createFormGroupFromObject(newCargo[key]);
                    if (currentGroup) currentGroup.addControl(key, form);
                    else this.form.addControl(key, form);
                } else {
                    if (currentGroup) currentGroup.addControl(key, this.formBuilder.control(newCargo[key]));
                    else this.form.addControl(key, this.formBuilder.control(newCargo[key]));
                }
            } else if (cargoValue !== undefined && formValue !== undefined) {
                if (Array.isArray(newCargo[key])) {
                    try {
                        const formArray = currentGroup.get(key) as FormArray;
                        newCargo[key].forEach((item, index) => {
                            if (!formArray.at(index)) {
                                if (typeof item === 'object' && item !== null) formArray.insert(index, this.utils.createFormGroupFromObject(item));
                                else formArray.insert(index, this.formBuilder.control(item));
                            }
                            this.setUnexistingElements(item, formArray.at(index) as FormGroup);
                        })
                    } catch (error) { /*formBuilder.array creó ese formArray como un formControl, por eso se genera ese error, pero no se pierde información */ }
                } else if (typeof newCargo[key] === 'object' && newCargo[key] !== null) {
                    const control = currentGroup.get(key);
                    if (control instanceof FormGroup)
                        this.setUnexistingElements(newCargo[key], control);
                }

            }
        });
    }

    /**
     * @description Creates form groups for origin addresses
     * @returns FormGroup with origin address fields
     */
    public createOriginAddressGroup(): FormGroup {
        return new ReactiveForm(this.formBuilder, this.serviceResources.cargoMock.cargoFeature.uploadDownload.origin.addresses[0]).form;
    }

    /** 
     * @description Creates form groups for destination
     * @returns FormGroup with destination fields
     */
    public createDestinationGroup(): FormGroup {
        return new ReactiveForm(this.formBuilder, this.serviceResources.cargoMock.cargoFeature.uploadDownload.destination[0]).form;
    }

    /**
     * @description Creates form groups for destination addresses
     * @returns FormGroup with destination address fields
     */
    public createDestinationAddressGroup(): FormGroup {
        const addressGroup = new ReactiveForm(this.formBuilder, this.serviceResources.cargoMock.cargoFeature.uploadDownload.destination[0].addresses[0]).form;
        this.ensureAddressContainersControl(addressGroup);
        return addressGroup;
    }

    private ensureAddressContainersControl(addressGroup: FormGroup): void {
        const currentValue = addressGroup.get('containers') ? addressGroup.get('containers').value : [];
        const containers = Array.isArray(currentValue) ? currentValue : [];
        addressGroup.setControl('containers', new FormControl(containers));
    }

    /**
     * @description Creates form groups for travel expenses
     * @returns FormGroup with travel expense fields
     */
    public createTravelExpenseGroup(): FormGroup {
        return this.formBuilder.group({
            cargoConsecutive: [this.cargo.consecutive],
            cargoId: [this.cargo.id],
            totalPaid: [0, [Validators.min(0)]],
            travelExpensesType: [{ id: "2", name: "Peaje" }],
        });
    }

    /**
     * @description Creates form groups for guide IDs
     * @returns FormGroup with guide ID fields
     */
    public createGuideRecipientGroup(): FormGroup {
        return this.formBuilder.group({
            name: [''],
            email: [''],
            phone: [''],
            clientId: ['', [Validators.pattern(/^$|^[A-Za-z0-9_-]{3,30}$/)]]
        });
    }

    public getConsignmentForm(): FormGroup {
        return this.consignmentManager.createConsignmentForm(this.cargo);
    }

    /**
     * @description Validates form data for each step
     * All isValid* methods follow the same pattern:
     * Gets the form control and returns its validity state
     */
    public isValidEntireCargo(cargo: Cargo = this.cargo, vehicle: Vehicle = this.cargoVehicle, consignments: ConsignmentCargo[] = this.cargoConsignments, ignorePermission: boolean = false): { isValid: boolean, error: string } {
        const isValidContractorTripInfo = this.isValidContractorTripInfo(cargo);
        if (!isValidContractorTripInfo.isValid) return isValidContractorTripInfo;
        const isValidOrigin = this.isValidOrigin(cargo, vehicle);
        if (!isValidOrigin.isValid) return isValidOrigin;
        const isValidDestination = this.isValidDestination(cargo, consignments);
        if (!isValidDestination.isValid) return isValidDestination;
        const isValidVehicleTypeInfo = this.isValidVehicleTypeInfo(cargo);
        if (!isValidVehicleTypeInfo.isValid) return isValidVehicleTypeInfo;
        const isValidEscortedVehicle = this.isValidEscortedVehicle(cargo);
        if (!isValidEscortedVehicle.isValid) return isValidEscortedVehicle;
        const isValidLoadTypeService = this.isValidLoadTypeService(cargo);
        if (!isValidLoadTypeService.isValid) return isValidLoadTypeService;
        const isValidLoadCharacteristics = this.isValidLoadCharacteristics(cargo);
        if (!isValidLoadCharacteristics.isValid) return isValidLoadCharacteristics;
        const isValidSatelliteLock = this.isValidSatelliteLock(cargo);
        if (!isValidSatelliteLock.isValid) return isValidSatelliteLock;
        const isValidServiceValue = this.isValidServiceValue(cargo, vehicle, ignorePermission);
        if (!isValidServiceValue.isValid) return isValidServiceValue;
        const isValidConsignmentsService = this.isValidConsignmentsService(cargo, consignments);
        if (!isValidConsignmentsService.isValid) return isValidConsignmentsService;
        const isValidServicePublication = this.isValidServicePublication(cargo);
        if (!isValidServicePublication.isValid) return isValidServicePublication;

        return { isValid: true, error: '' };
    }

    public isValidContractorTripInfo(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const isValidAdditionalInformation = this.isValidContractorAdditionalInformation(cargo);
        if (!isValidAdditionalInformation.isValid) return isValidAdditionalInformation;
        return this.isValidContractorDetails(cargo);
    }

    public isValidContractorAdditionalInformation(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const isValidTripType = this.isValidTripType(cargo);
        if (!isValidTripType.isValid) return isValidTripType;
        return this.isValidMinistry(cargo);
    }

    public isValidContractorDetails(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const isValidCargoOwnerName = this.isValidCargoOwnerName(cargo);
        if (!isValidCargoOwnerName.isValid) return isValidCargoOwnerName;
        const isValidCargoOwnerBranchOffice = this.isValidCargoOwnerBranchOffice(cargo);
        if (!isValidCargoOwnerBranchOffice.isValid) return isValidCargoOwnerBranchOffice;
        if (this.requiresTransportCompany(cargo)) {
            const transportCompany = this.utils.getNestedValue(cargo, 'transportCompany');
            if (!transportCompany || !transportCompany.id)
                return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'la empresa de transporte') };
        }

        return { isValid: true, error: '' };
    }

    /**
     * La empresa de transporte solo la declara un servicio de transporte dentro
     * de una SaaS que preste ese tipo de servicio. Un servicio de escolta o de
     * candado satelital no tiene transportadora que asignar.
     */
    public requiresTransportCompany(cargo: Cargo = this.cargo): boolean {
        return !!(this.authService && this.authService.hasTransportServicesCompany)
            && this.utils.isTransportService(cargo);
    }

    public isValidTripType(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const tripType = this.utils.getNestedValue(cargo, 'cargoModel.tripType.name');
        if (!tripType) return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'el tipo de viaje') };
        return { isValid: true, error: '' };
    }
    public isRequiredMinistry(cargo: Cargo = this.cargo): boolean {
        return this.isTransportService(cargo);
    }
    public isValidMinistry(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const ministry = this.utils.getNestedValue(cargo, 'ministry');
        if (!this.utils.isDefined(ministry) && this.isRequiredMinistry(cargo))
            return { isValid: false, error: "Se requiere guardar nuevamente el requerimiento de manifiesto de carga" };
        return { isValid: true, error: '' };
    }
    public isValidCargoOwnerName(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const ministry = this.utils.getNestedValue(cargo, 'ministry');
        const cargoOwner = this.utils.getNestedValue(cargo, 'cargoOwner');
        if (ministry) {
            if (!cargoOwner || !cargoOwner.name)
                return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'el nombre del contratante') };
            if (!cargoOwner.documentNumber || !cargoOwner.documentType)
                return { isValid: false, error: "Se requiere volver a seleccionar al contratante del servicio" };
        }
        const idCompany = this.utils.getNestedValue(cargo, 'idCompany');
        if (!idCompany)
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'el nombre del contratante') };
        return { isValid: true, error: '' };
    }
    public isRequiredCargoOwnerBranchOffice(cargo: Cargo = this.cargo): boolean {
        const ministry = this.utils.getNestedValue(cargo, 'ministry');
        return !!ministry;
    }
    public isValidCargoOwnerBranchOffice(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const cargoOwner = this.utils.getNestedValue(cargo, 'cargoOwner');
        if ((!cargoOwner || !cargoOwner.branchOffice) && this.isRequiredCargoOwnerBranchOffice(cargo))
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'la dirección del contratante') };
        return { isValid: true, error: '' };
    }

    public isRequiredCargoTags(): boolean {
        return true;
    }

    public isValidOrigin(cargo: Cargo = this.cargo, vehicle: Vehicle = this.cargoVehicle): { isValid: boolean, error: string } {
        const isValidOriginCity = this.isValidOriginCity(cargo);
        if (!isValidOriginCity.isValid) return isValidOriginCity;
        const isValidDateLoad = this.isValidDateLoad(cargo);
        if (!isValidDateLoad.isValid) return isValidDateLoad;
        const isValidReteica = this.isValidReteica(cargo, vehicle);
        if (!isValidReteica.isValid) return isValidReteica;
        const isValidOriginAddresses = this.isValidOriginAddresses(cargo);
        if (!isValidOriginAddresses.isValid) return isValidOriginAddresses;
        return { isValid: true, error: '' };
    }

    public isValidOriginCity(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const originCity = this.utils.getNestedValue(cargo, 'cargoFeature.uploadDownload.origin.name');
        if (!originCity)
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'la ciudad de origen') };
        const country = this.utils.getNestedValue(cargo, 'cargoFeature.uploadDownload.origin.country');
        if (!country || !country.code || !country.name)
            return { isValid: false, error: "Se requiere volver a seleccionar la ciudad de origen" };
        const municipalityCode = this.utils.getNestedValue(cargo, 'cargoFeature.uploadDownload.origin.municipalityCode');
        if (!municipalityCode && country.code === 'CO')
            return { isValid: false, error: "Se requiere volver a seleccionar la ciudad de origen" };
        return { isValid: true, error: '' };
    }

    public isValidDateLoad(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const dateLoad = this.utils.getNestedValue(cargo, 'dateLoad');
        if (!dateLoad)
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'la fecha de origen') };
        const permissionIgnoreDate = this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.changeCargoLoadDate);
        if (this.isTransportService(cargo) && DateManager.isBefore(DateManager.stringToDate(dateLoad), new Date()) &&
            [CargoStateEnum.ACCEPTED, CargoStateEnum.CREATED, CargoStateEnum.REQUEST].includes(cargo.state) && !permissionIgnoreDate)
            return { isValid: false, error: "La fecha de origen no puede ser anterior a la fecha actual" };
        return { isValid: true, error: '' };
    }

    public isRequiredReteica(cargo: Cargo = this.cargo, vehicle: Vehicle = this.cargoVehicle): boolean {
        const ministry = this.utils.getNestedValue(cargo, 'ministry');
        return !!ministry && !this.hasTaxExceptions(cargo, vehicle);
    }

    public isValidReteica(cargo: Cargo = this.cargo, vehicle: Vehicle = this.cargoVehicle): { isValid: boolean, error: string } {
        const reteica = this.utils.getNestedValue(cargo, 'shippingCost.reteicaPercentage');
        if (this.isRequiredReteica(cargo, vehicle) && !reteica)
            return { isValid: false, error: "La ciudad de origen no registra un ReteIca, si esto es incorrecto vuelva a seleccionarla, de lo contrario asocielo o cambie de ciudad" };
        return { isValid: true, error: '' };
    }

    public isValidOriginAddresses(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const originAddresses: AddressCargo[] = this.utils.getNestedValue(cargo, 'cargoFeature.uploadDownload.origin.addresses');
        if (!originAddresses || !originAddresses.length)
            return { isValid: false, error: "No hay direcciones de origen registradas" };
        for (let index = 0; index < originAddresses.length; index++) {
            const address = originAddresses[index];
            if (address && !address.time)
                return { isValid: false, error: `Se requiere volver a seleccionar la hora de la dirección ${index + 1}` };

            if (address && address.cellphone && address.cellphone.length < 10)
                return { isValid: false, error: this.utils.giveMessageError('MIN_LENGTH_NOT_REACHED', `celular de la dirección ${index + 1}`, 10) };

            if (!this.isTransportService(cargo) && (!address.thirdPartyConsignment || !address.thirdPartyConsignment.documentNumber || !address.thirdPartyConsignment.documentType || !address.thirdPartyConsignment.name))
                return { isValid: false, error: `Se requiere seleccionar el remitente de la dirección ${index + 1}` };

            if (!address || !address.address)
                return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', `la dirección ${index + 1}`) };

            if (address && (!address.location || !address.location.lat || !address.location.lng))
                return { isValid: false, error: `Se requiere volver a seleccionar la dirección ${index + 1}` };

            if (!this.isTransportService(cargo) && !address.thirdPartyConsignment.branchOffice)
                return { isValid: false, error: `Se requiere seleccionar la dirección ${index + 1}` };
        };
        return { isValid: true, error: '' };
    }

    public isValidDestination(cargo: Cargo = this.cargo, consignments: ConsignmentCargo[] = this.cargoConsignments): { isValid: boolean, error: string } {
        const isValidDestinationCities = this.isValidDestinationCities(cargo);
        if (!isValidDestinationCities.isValid) return isValidDestinationCities;
        const isValidDestinationAddresses = this.isValidDestinationAddresses(cargo, consignments);
        if (!isValidDestinationAddresses.isValid) return isValidDestinationAddresses;
        return { isValid: true, error: '' };
    }

    public isValidDestinationCities(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const destinationCities: Destination[] = this.utils.getNestedValue(cargo, 'cargoFeature.uploadDownload.destination');
        if (!destinationCities || !destinationCities.length)
            return { isValid: false, error: "No hay ciudades de destino registradas" };

        const tripType = this.utils.getNestedValue(cargo, 'cargoModel.tripType.name');
        const originCity = this.utils.getNestedValue(cargo, 'cargoFeature.uploadDownload.origin.name');
        const destinationCitiesNames = [];
        let lastDate = cargo.dateLoad ? DateManager.stringToDate(cargo.dateLoad) : new Date();
        for (let index = 0; index < destinationCities.length; index++) {
            const destinationCity = destinationCities[index];
            if (!destinationCity || !destinationCity.name)
                return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', `la ciudad de destino ${index + 1}`) };
            if (destinationCity && (!destinationCity.country || !destinationCity.country.code || !destinationCity.country.name))
                return { isValid: false, error: `Se requiere volver a seleccionar la ciudad ${index + 1} de destino` };
            if (destinationCity && destinationCity.country && destinationCity.country.code === 'CO' && !destinationCity.municipalityCode)
                return { isValid: false, error: `Se requiere volver a seleccionar la ciudad ${index + 1} de destino` };
            if (originCity && tripType && ["Urbana", "Última milla"].includes(tripType) && destinationCity.name !== originCity)
                return { isValid: false, error: `El tipo de viaje "${tripType === 'Última milla' ? 'Última milla / Paqueteo urbano' : tripType}" debe tener como origen y destino la misma ciudad` };
            destinationCitiesNames.push(destinationCity.name);
            if (destinationCity && !destinationCity.downloadDate)
                return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', `la fecha del destino ${index + 1}`) };
            if (destinationCity && DateManager.isBefore(DateManager.stringToDate(destinationCity.downloadDate), lastDate))
                return { isValid: false, error: `La fecha del destino ${index + 1} no puede ser anterior a la fecha ${index ? `del destino ${index}` : 'de origen'}` };
            lastDate = DateManager.stringToDate(destinationCity.downloadDate);
        };
        if (originCity && tripType && ["Nacional", "Internacional"].includes(tripType) && destinationCitiesNames.every(destination => destination && destination === originCity))
            return { isValid: false, error: `El tipo de viaje "${tripType}" debe tener como origen y destino ciudades distintas` };

        return { isValid: true, error: '' };
    }

    public isValidDestinationAddresses(cargo: Cargo = this.cargo, consignments: ConsignmentCargo[] = this.cargoConsignments): { isValid: boolean, error: string } {
        const destinations: Destination[] = this.utils.getNestedValue(cargo, 'cargoFeature.uploadDownload.destination');
        const ministry = this.utils.getNestedValue(cargo, 'ministry');
        if (!destinations || !destinations.length)
            return { isValid: false, error: "No hay ciudades de destino registradas" };

        let lastDate = cargo.dateLoad ? DateManager.stringToDate(cargo.dateLoad) : new Date();
        for (let index = 0; index < destinations.length; index++) {
            const destination = destinations[index];
            if (!destination || !destination.addresses || !destination.addresses.length)
                return { isValid: false, error: `No hay direcciones registradas para el destino ${index + 1}` };

            for (let indexAddress = 0; indexAddress < destination.addresses.length; indexAddress++) {
                const address = destination.addresses[indexAddress];
                if (address && !address.time)
                    return { isValid: false, error: `Se requiere volver a seleccionar la hora de la dirección ${indexAddress + 1} del destino ${index + 1}` };

                if (address && address.cellphone && address.cellphone.length < 10)
                    return { isValid: false, error: this.utils.giveMessageError('MIN_LENGTH_NOT_REACHED', `celular de la dirección ${indexAddress + 1} del destino ${index + 1}`, 10) };

                if (!this.isTransportService(cargo) && (!address.thirdPartyConsignment || !address.thirdPartyConsignment.documentNumber || !address.thirdPartyConsignment.documentType || !address.thirdPartyConsignment.name))
                    return { isValid: false, error: `Se requiere seleccionar el destinatario de la dirección ${indexAddress + 1} del destino ${index + 1}` };

                if (!address || !address.address)
                    return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', `la dirección ${indexAddress + 1} del destino ${index + 1}`) };

                if (address && (!address.location || !address.location.lat || !address.location.lng))
                    return { isValid: false, error: `Se requiere volver a seleccionar la dirección ${indexAddress + 1} del destino ${index + 1}` };

                if (!this.isTransportService(cargo) && !address.thirdPartyConsignment.branchOffice)
                    return { isValid: false, error: `Se requiere seleccionar la dirección ${indexAddress + 1} del destino ${index + 1}` };

                if (address.consignments && address.consignments.length && (!ministry || address.consignments.some(consignmentId => !consignments.find(consignment => consignment.id === consignmentId))))
                    return { isValid: false, error: `Se requiere volver a guardar las direcciones de destino` };
                if (destination && !destination.downloadDate)
                    return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', `la fecha del destino ${index + 1}`) };
                const destinationDate = DateManager.stringToDate(`${DateManager.setStartOfDay(DateManager.stringToDate(destination.downloadDate)).toISOString().split('T')[0]}T${address.time} -0500`, 'YYYY-MM-DDTHH:mm:ssZ');
                if (destination && DateManager.isBefore(destinationDate, lastDate))
                    return { isValid: false, error: `La fecha del destino ${index + 1} no puede ser anterior a la fecha ${index ? `del destino ${index}` : 'de origen'}` };
                lastDate = destinationDate;

                const recipients = address && address.guidesRecipients ? address.guidesRecipients : [];
                if (recipients && recipients.length) {
                    for (let k = 0; k < recipients.length; k++) {
                        const recipient = recipients[k];
                        const id = recipient && recipient.clientId ? String(recipient.clientId).trim() : '';
                        if (id && (id.length < 3 || id.length > 30 || !/^[A-Za-z0-9_-]*$/.test(id)))
                            return { isValid: false, error: `La guia ${k + 1} de la dirección ${indexAddress + 1} del destino ${index + 1} no cumple con los requisitos` };
                    }
                }
            };
        };
        return { isValid: true, error: '' };
    }

    /**
     * @description Validates vehicle type information
     * @returns { isValid: boolean, error: string } indicating if vehicle type information is valid
     */
    public isValidVehicleTypeInfo(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        if (this.isSatelliteLockService(cargo)) return { isValid: true, error: '' };
        const isValidVehicleType = this.isValidVehicleType(cargo);
        if (!isValidVehicleType.isValid) return isValidVehicleType;
        const isValidVehicleTypeBodyWorkType = this.isValidVehicleTypeBodyWorkType(cargo);
        if (!isValidVehicleTypeBodyWorkType.isValid) return isValidVehicleTypeBodyWorkType;
        const isValidEnvironmentType = this.isValidEnvironmentType(cargo);
        if (!isValidEnvironmentType.isValid) return isValidEnvironmentType;
        const isValidVehicleAxles = this.isValidVehicleAxles(cargo);
        if (!isValidVehicleAxles.isValid) return isValidVehicleAxles;
        return { isValid: true, error: '' };
    }
    public isValidVehicleType(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const vehicleType = this.utils.getNestedValue(cargo, 'cargoFeature.vehicleType.name');
        if (!vehicleType)
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'el tipo de vehiculo') };
        return { isValid: true, error: '' };
    }
    public isValidVehicleTypeBodyWorkType(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const bodyWorkType = this.utils.getNestedValue(cargo, 'cargoFeature.vehicleType.bodyWorkType.name');
        if (!bodyWorkType)
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'el tipo de carroceria') };
        return { isValid: true, error: '' };
    }
    public isRequiredEnvironmentType(cargo: Cargo = this.cargo): boolean {
        const vehicleType = this.utils.getNestedValue(cargo, 'cargoFeature.vehicleType.name');
        const bodyWorkType = this.utils.getNestedValue(cargo, 'cargoFeature.vehicleType.bodyWorkType.name');
        return this.cargoManager.requestEnvironmentType(vehicleType, bodyWorkType);
    }
    public isValidEnvironmentType(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        if (!this.isRequiredEnvironmentType(cargo)) return { isValid: true, error: '' };
        const environmentType = this.utils.getNestedValue(cargo, 'cargoFeature.vehicleType.quality');
        if (!environmentType)
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'el tipo de ambiente') };
        return { isValid: true, error: '' };
    }
    public isRequiredVehicleAxles(cargo: Cargo = this.cargo): boolean {
        const vehicleType = this.utils.getNestedValue(cargo, 'cargoFeature.vehicleType.name');
        return vehicleType && !vehicleAxles[vehicleType];
    }
    public isValidVehicleAxles(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        if (!this.isRequiredVehicleAxles(cargo)) return { isValid: true, error: '' };
        const vehicleAxles = this.utils.getNestedValue(cargo, 'axles');
        if (!vehicleAxles)
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'el numero de ejes') };
        if (vehicleAxles < 2)
            return { isValid: false, error: 'El número de ejes debe ser mayor o igual a 2' };
        const vehicleType = this.utils.getNestedValue(cargo, 'cargoFeature.vehicleType.name');
        if (vehicleAxles[vehicleType] && vehicleAxles !== vehicleAxles[vehicleType])
            return { isValid: false, error: "Se requiere volver a seleccionar el vehículo del servicio" };
        return { isValid: true, error: '' };
    }
    public isRequiredTrailer(cargo: Cargo = this.cargo): boolean {
        const vehicleType = this.utils.getNestedValue(cargo, 'cargoFeature.vehicleType.name');
        const bodyWorkType = this.utils.getNestedValue(cargo, 'cargoFeature.vehicleType.bodyWorkType.name');
        return vehicleType && bodyWorkType && vehicleType === 'TRACTOCAMION' && bodyWorkType !== 'GRUA';
    }
    public isValidTrailer(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        if (!this.isRequiredTrailer(cargo)) return { isValid: true, error: '' };
        return { isValid: true, error: '' };
    }
    public isValidTrailerAxles(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        if (!this.isRequiredTrailer(cargo)) return { isValid: true, error: '' };
        const trailerAxles = this.utils.getNestedValue(cargo, 'cargoFeature.vehicleType.trailer.axles');
        if (trailerAxles < 2)
            return { isValid: false, error: 'El número de ejes del tráiler debe ser mayor o igual a 2' };
        return { isValid: true, error: '' };
    }

    public isValidEscortedVehicle(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        if (this.isTransportService(cargo)) return { isValid: true, error: '' };
        const isValidEscortedLicensePlate = this.isValidEscortedLicensePlate(cargo);
        if (!isValidEscortedLicensePlate.isValid) return isValidEscortedLicensePlate;
        return { isValid: true, error: '' };
    }

    public isValidEscortedLicensePlate(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const licensePlate = this.utils.getNestedValue(cargo, 'escortedVehicle.licensePlate');
        const fieldToValidate = this.isSatelliteLockService(cargo) ? 'la placa del vehiculo a seguir' : 'la placa del vehiculo a escoltar';
        if (!licensePlate || !licensePlate.trim())
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', fieldToValidate) };
        return { isValid: true, error: '' };
    }

    public isValidEscortedDriverName(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        return { isValid: true, error: '' };
    }

    public isValidEscortedPhoneNumber(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        return { isValid: true, error: '' };
    }


    public isRequiredLoadTypeService(cargo: Cargo = this.cargo): boolean {
        return this.isTransportService(cargo);
    }

    public isValidLoadTypeService(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        if (!this.isRequiredLoadTypeService(cargo)) return { isValid: true, error: '' };

        const isValidLetterRetirement = this.isValidLetterRetirement(cargo);
        if (!isValidLetterRetirement.isValid) return isValidLetterRetirement;
        const isValidRegimeType = this.isValidRegimeType(cargo);
        if (!isValidRegimeType.isValid) return isValidRegimeType;
        const isValidDateVesselArrival = this.isValidDateVesselArrival(cargo);
        if (!isValidDateVesselArrival.isValid) return isValidDateVesselArrival;
        const isValidLoadType = this.isValidLoadType(cargo);
        if (!isValidLoadType.isValid) return isValidLoadType;
        const isValidServiceType = this.isValidServiceType(cargo);
        if (!isValidServiceType.isValid) return isValidServiceType;
        const isValidContainerSize = this.isValidContainerSize(cargo);
        if (!isValidContainerSize.isValid) return isValidContainerSize;

        return { isValid: true, error: '' };
    }

    public isRequiredLetterRetirement(cargo: Cargo = this.cargo): boolean {
        const tripType = this.utils.getNestedValue(cargo, 'cargoModel.tripType.name');
        return tripType === 'Exportación';
    }

    public isValidLetterRetirement(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const letterRetirement = this.utils.getNestedValue(cargo, 'cargoModel.tripType.letterRetirement');
        if (this.isRequiredLetterRetirement(cargo) && !letterRetirement)
            return { isValid: false, error: 'Falta subir la carta de retiro' };
        return { isValid: true, error: '' };
    }

    public isRequiredRegimeType(cargo: Cargo = this.cargo): boolean {
        return this.isImportOrExport(cargo);
    }

    public isValidRegimeType(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const tripType = this.utils.getNestedValue(cargo, 'cargoModel.tripType.name');
        const regimeType = this.utils.getNestedValue(cargo, 'cargoModel.tripType.regimeType');
        if (this.isRequiredRegimeType(cargo) && !regimeType)
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'el tipo de regimen') };
        if (this.regimeTypes[tripType] && !this.regimeTypes[tripType].includes(regimeType))
            return { isValid: false, error: "Es necesario volver a seleccionar el tipo de regimen" };
        return { isValid: true, error: '' };
    }

    public isRequiredDateVesselArrival(cargo: Cargo = this.cargo): boolean {
        const regimeType = this.utils.getNestedValue(cargo, 'cargoModel.tripType.regimeType');
        return this.isImportOrExport(cargo) && this.regimeTypesWithDateVesselArrival.includes(regimeType);
    }

    public isValidDateVesselArrival(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const dateVesselArrival = this.utils.getNestedValue(cargo, 'cargoModel.tripType.date');
        if (this.isRequiredDateVesselArrival(cargo) && !dateVesselArrival)
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'la fecha de descargue del buque') };
        return { isValid: true, error: '' };
    }

    public isRequiredLoadType(cargo: Cargo = this.cargo): boolean {
        const tripType = this.utils.getNestedValue(cargo, 'cargoModel.tripType.name');
        return tripType !== 'Última milla';
    }

    public isValidLoadType(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const loadType = this.utils.getNestedValue(cargo, 'cargoModel.cargoType.name');
        const tripType = this.utils.getNestedValue(cargo, 'cargoModel.tripType.name');
        if (!loadType)
            return {
                isValid: false, error: tripType === 'Última milla'
                    ? "Se requiere volver a guardar el tipo de servicio"
                    : this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'el tipo de carga')
            };
        if (tripType === 'Última milla' && loadType !== 'Carga suelta')
            return { isValid: false, error: "Se requiere volver a guardar el tipo de servicio" };

        return { isValid: true, error: '' };
    }

    public isValidServiceType(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const tripType = this.utils.getNestedValue(cargo, 'cargoModel.tripType.name');
        const cargoType = this.utils.getNestedValue(cargo, 'cargoModel.cargoType.name');
        const serviceType = this.utils.getNestedValue(cargo, 'cargoModel.serviceType.name');
        if (!serviceType)
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'el tipo de servicio') };
        if (tripType === 'Última milla' && cargoType === 'Contenedor')
            return { isValid: false, error: "Se requiere volver a seleccionar el tipo de servicio" };
        const selectedService = this.serviceTypes.find(type => type.name === cargoType);
        if (selectedService && !selectedService.types.includes(serviceType))
            return { isValid: false, error: "Se requiere volver a seleccionar el tipo de servicio" };
        return { isValid: true, error: '' };
    }

    public isRequiredContainerSize(cargo: Cargo = this.cargo): boolean {
        const tripType = this.utils.getNestedValue(cargo, 'cargoModel.tripType.name');
        const cargoType = this.utils.getNestedValue(cargo, 'cargoModel.cargoType.name');
        return tripType && tripType !== 'Última milla' && cargoType === 'Contenedor';
    }

    public isValidContainerSize(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const containerSize = this.utils.getNestedValue(cargo, 'cargoModel.cargoType.containerMeasure.size');
        if (this.isRequiredContainerSize(cargo) && !containerSize)
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'las medidas del contenedor') };
        return { isValid: true, error: '' };
    }

    public isContainerService(cargo: Cargo = this.cargo): boolean {
        const cargoType = this.utils.getNestedValue(cargo, 'cargoModel.cargoType.name');
        return cargoType === 'Contenedor';
    }

    public isValidLoadCharacteristics(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        if (!this.isTransportService(cargo)) return { isValid: true, error: '' };

        // El perfil de riesgo ya no se valida en creacion: se calcula automaticamente en backend.
        const isValidContainerInfo = this.isValidContainerInfo(cargo);
        if (!isValidContainerInfo.isValid) return isValidContainerInfo;
        const isValidAmount = this.isValidAmount(cargo);
        if (!isValidAmount.isValid) return isValidAmount;
        const isValidTotalMeasurement = this.isValidTotalMeasurement(cargo);
        if (!isValidTotalMeasurement.isValid) return isValidTotalMeasurement;
        const isValidQuantity = this.isValidQuantity();
        if (!isValidQuantity.isValid) return isValidQuantity;
        const isValidTotalWeight = this.isValidTotalWeight(cargo);
        if (!isValidTotalWeight.isValid) return isValidTotalWeight;
        const isValidUnit = this.isValidUnit(cargo);
        if (!isValidUnit.isValid) return isValidUnit;
        const isValidType = this.isValidType(cargo);
        if (!isValidType.isValid) return isValidType;

        return { isValid: true, error: '' };
    }

    public isValidRiskProfile(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        // El perfil de riesgo se calcula automaticamente en backend (empresa + valor declarado),
        // por lo que ya no es un campo requerido en la creacion del servicio.
        return { isValid: true, error: '' };
    }

    public isRequiredContainerInfo(cargo: Cargo = this.cargo): boolean {
        return this.isContainerService(cargo) && !this.utils.getNestedValue(cargo, 'ministry');
    }

    public isValidContainerInfo(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        if (!this.isRequiredContainerInfo(cargo)) return { isValid: true, error: '' };
        const container = this.utils.getNestedValue(cargo, 'container');
        if (!container) return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'el contenedor') };
        return { isValid: true, error: '' };
    }

    public isRequiredMerchandiseCharacteristics(cargo: Cargo = this.cargo): boolean {
        return this.isTransportService(cargo);
    }

    public isValidAmount(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        if (!this.isRequiredMerchandiseCharacteristics(cargo)) return { isValid: true, error: '' };
        const amount = this.utils.getNestedValue(cargo, 'cargoFeature.cargoMeasure.amount');
        if (amount && this.isEmptyContainer(cargo))
            return { isValid: false, error: CargoMessages.AMOUNT_TO_EMPTY_CONTAINERS };
        return { isValid: true, error: '' };
    }

    public isValidTotalMeasurement(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        if (!this.isRequiredMerchandiseCharacteristics(cargo)) return { isValid: true, error: '' };
        const totalMeasurement = this.utils.getNestedValue(cargo, 'cargoFeature.cargoMeasure.totalMeasurement');
        if (!this.utils.isDefined(totalMeasurement) || typeof totalMeasurement !== 'number')
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'la medida total') };
        return { isValid: true, error: '' };
    }

    public isValidQuantity(): { isValid: boolean, error: string } {
        return { isValid: true, error: '' };
    }

    public isValidTotalWeight(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        if (!this.isRequiredMerchandiseCharacteristics(cargo)) return { isValid: true, error: '' };
        const totalWeigth = this.utils.getNestedValue(cargo, 'cargoFeature.cargoMeasure.totalWeigth');
        if (!this.utils.isDefined(totalWeigth) || typeof totalWeigth !== 'number')
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'el peso total') };
        if (totalWeigth < 0)
            return { isValid: false, error: this.utils.giveMessageError('MIN_VALUE_NOT_REACHED', 'peso total', 0) };
        if (totalWeigth > AmountsCargoEnum.MAX_WEIGHT_ALLOWED_KG)
            return { isValid: false, error: this.utils.giveMessageError('MAX_VALUE_EXCEED', 'peso total', AmountsCargoEnum.MAX_WEIGHT_ALLOWED_KG) };
        if (!totalWeigth && !this.isEmptyContainer(cargo))
            return { isValid: false, error: CargoMessages.NO_TOTAL_WEIGHT };
        if (totalWeigth && this.isEmptyContainer(cargo))
            return { isValid: false, error: CargoMessages.TOTAL_WEIGHT_TO_EMPTY_CONTAINERS };
        const unit = this.utils.getNestedValue(cargo, 'cargoFeature.cargoMeasure.unit');
        if ((unit === "1" && totalWeigth > AmountsCargoEnum.MAX_WEIGHT_ALLOWED_KG) || (unit === "2" && totalWeigth > AmountsCargoEnum.MAX_WEIGHT_ALLOWED_GL))
            return { isValid: false, error: CargoMessages.OVERWEIGHT + (unit === "1" ? AmountsCargoEnum.MAX_WEIGHT_ALLOWED_KG : AmountsCargoEnum.MAX_WEIGHT_ALLOWED_GL) };
        return { isValid: true, error: '' };
    }

    public isValidUnit(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        if (!this.isRequiredMerchandiseCharacteristics(cargo)) return { isValid: true, error: '' };
        const unit = this.utils.getNestedValue(cargo, 'cargoFeature.cargoMeasure.unit');
        const totalWeigth = this.utils.getNestedValue(cargo, 'cargoFeature.cargoMeasure.totalWeigth');
        if (!unit && totalWeigth !== 0)
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'la unidad de medida') };
        return { isValid: true, error: '' };
    }
    public isValidType(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        if (!this.isRequiredMerchandiseCharacteristics(cargo)) return { isValid: true, error: '' };
        const type = this.utils.getNestedValue(cargo, 'cargoFeature.cargoMeasure.type');
        const totalWeigth = this.utils.getNestedValue(cargo, 'cargoFeature.cargoMeasure.totalWeigth');
        if (!type && totalWeigth !== 0)
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'el tipo de almacenamiento') };
        return { isValid: true, error: '' };
    }

    public hasTaxExceptions(cargo: Cargo = this.cargo, vehicle: Vehicle = this.cargoVehicle): boolean {
        if (this.utils.getNestedValue(cargo, 'simpleRegimen')) return true;
        return this.hasOwnFleetException(cargo, vehicle);
    }

    public hasPermissionNoMinimumUtility(cargo: Cargo = this.cargo, ignorePermission?: boolean): boolean {
        if (ignorePermission) return true;
        const hasPermissionZero = this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.createCargoWithRateOrCostZero);
        if (hasPermissionZero) return true;
        const tripType = this.utils.getNestedValue(cargo, 'cargoFeature.tripType.name');
        const serviceType = this.utils.getNestedValue(cargo, 'cargoFeature.serviceType.name');
        const hasPermission = this.permissionRole.hasPermission(
            this.permission.cargo.module,
            this.permission.cargo.createCargosWithoutMinimumUtility
        );
        return tripType === "Urbana" && serviceType.includes("Contenedor vacío") && hasPermission;
    }

    public hasOwnFleetException(cargo: Cargo = this.cargo, vehicle: Vehicle = this.cargoVehicle): boolean {
        const companyOwnFleet = this.authService && this.authService.hasCompanySettingEnabled(SettingId.OWN_FLEET);
        return (companyOwnFleet && !this.utils.getNestedValue(cargo, 'licensePlate'))
            || this.utils.vehicleIsOwnFleet(vehicle, this.authService && this.authService.getCompanySaaSConfig() ? this.authService.getCompanySaaSConfig() : null);
    }

    public isValidSatelliteLock(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        if (!this.isSatelliteLockService(cargo)) return { isValid: true, error: '' };
        const cargoLock: ServiceLock = this.utils.getNestedValue(cargo, 'lock');
        if (!cargoLock || !cargoLock.type || !cargoLock.type.id || !cargoLock.type.name)
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'el tipo de candado satelital') };
        return { isValid: true, error: '' };
    }

    public isValidServiceValue(cargo: Cargo = this.cargo, vehicle: Vehicle = this.cargoVehicle, ignorePermission?: boolean): { isValid: boolean, error: string } {
        const isValidServiceRate = this.isValidServiceRate(cargo, vehicle, ignorePermission);
        if (!isValidServiceRate.isValid) return isValidServiceRate;
        const isValidFreightCost = this.isValidFreightCost(cargo, vehicle, ignorePermission);
        if (!isValidFreightCost.isValid) return isValidFreightCost;
        const isValidAdvancePercentage = this.isValidAdvancePercentage(cargo);
        if (!isValidAdvancePercentage.isValid) return isValidAdvancePercentage;
        const isValidPaymentTime = this.isValidPaymentTime(cargo);
        if (!isValidPaymentTime.isValid) return isValidPaymentTime;
        return { isValid: true, error: '' };
    }

    public isValidServiceRate(cargo: Cargo = this.cargo, vehicle: Vehicle = this.cargoVehicle, ignorePermission?: boolean): { isValid: boolean, error: string } {
        const rate = this.utils.getNestedValue(cargo, 'shippingCost.rate');
        if (!this.utils.isDefined(rate) || typeof rate !== 'number')
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'la tarifa') };
        const hasExceptionRateOrCost = this.hasPermissionNoMinimumUtility(cargo, ignorePermission) || this.hasOwnFleetException(cargo, vehicle);
        const minRate = hasExceptionRateOrCost && !this.isSatelliteLockService(cargo) ? 0 : 1;
        if (rate < minRate) return { isValid: false, error: this.utils.giveMessageError('MIN_VALUE_NOT_REACHED', 'tarifa', minRate) };
        return { isValid: true, error: '' };
    }

    public isValidFreightCost(cargo: Cargo = this.cargo, vehicle: Vehicle = this.cargoVehicle, ignorePermission?: boolean): { isValid: boolean, error: string } {
        const freightCost = this.utils.getNestedValue(cargo, 'shippingCost.freightCost');
        if (!this.utils.isDefined(freightCost) || typeof freightCost !== 'number')
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'el flete') };
        const minFreightCost = this.hasPermissionNoMinimumUtility(cargo, ignorePermission) || this.hasOwnFleetException(cargo, vehicle) || !this.isTransportService(cargo) ? 0 : 1;
        if (freightCost < minFreightCost) return { isValid: false, error: this.utils.giveMessageError('MIN_VALUE_NOT_REACHED', 'flete', minFreightCost) };
        if (this.isTransportService(cargo)) {
            const idCompany = this.utils.getNestedValue(cargo, 'idCompany');
            const minUtility = this.companyManager.getUtilityByCompanyId(idCompany);
            if (minUtility && this.utilityCargo < minUtility && !(this.hasPermissionNoMinimumUtility(cargo, ignorePermission) || this.hasOwnFleetException(cargo, vehicle)))
                return { isValid: false, error: Fmt.string(FormMessages.MINIMUN_UTILITY_NOT_REACHED, minUtility) };
        }
        return { isValid: true, error: '' };
    }

    public isValidAdvancePercentage(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const advancePercentage = this.utils.getNestedValue(cargo, 'shippingCost.advancePercentage');
        if (!this.utils.isDefined(advancePercentage) || typeof advancePercentage !== 'number')
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'el porcentaje de anticipo') };
        if (advancePercentage < 0)
            return { isValid: false, error: this.utils.giveMessageError('MIN_VALUE_NOT_REACHED', 'porcentaje de anticipo', 0) };
        const maxAdvancePercentage = Math.min(this.companyManager.getAdvancePercentageByCompanyId(this.utils.getNestedValue(cargo ? cargo : this.cargo, 'idCompany')), 100);
        if (advancePercentage > maxAdvancePercentage)
            return { isValid: false, error: this.utils.giveMessageError('MAX_VALUE_EXCEED', 'porcentaje de anticipo', maxAdvancePercentage) };
        return { isValid: true, error: '' };
    }

    public isValidPaymentTime(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const paymentTime = this.utils.getNestedValue(cargo, 'shippingCost.paymentTime');
        if (!this.utils.isDefined(paymentTime) || typeof paymentTime !== 'number')
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'el tiempo de pago') };
        if (paymentTime <= 0 && this.isTransportService(cargo))
            return { isValid: false, error: this.utils.giveMessageError('MIN_VALUE_NOT_REACHED', 'tiempo de pago', 1) };
        if (paymentTime > 60 && !!cargo.ministry)
            return { isValid: false, error: this.utils.giveMessageError('MAX_VALUE_EXCEED', 'tiempo de pago', 60) };
        return { isValid: true, error: '' };
    }

    public isRequiredAdditionalServices(cargo: Cargo = this.cargo): boolean {
        return this.isTransportService(cargo);
    }

    public isRequiredTravelExpenses(cargo: Cargo = this.cargo): boolean {
        return this.isTransportService(cargo) && this.authService.hasCompanySettingEnabled(SettingId.USE_TRAVEL_EXPENSES);
    }

    public isRequiredConsignmentsService(cargo: Cargo = this.cargo): boolean {
        const ministry = this.utils.getNestedValue(cargo, 'ministry');
        return this.isTransportService(cargo) && !!ministry;
    }

    public isValidConsignmentsService(cargo: Cargo = this.cargo, consignments: ConsignmentCargo[] = this.cargoConsignments): { isValid: boolean, error: string } {
        if (!this.isRequiredConsignmentsService(cargo)) return { isValid: true, error: '' };
        const isValidManifestType = this.isValidManifestType(cargo);
        if (!isValidManifestType.isValid) return isValidManifestType;
        const isValidMonitoringCompany = this.isValidMonitoringCompany(cargo);
        if (!isValidMonitoringCompany.isValid) return isValidMonitoringCompany;
        const isValidConsignments = this.isValidConsignments(cargo, consignments);
        if (!isValidConsignments.isValid) return isValidConsignments;
        return { isValid: true, error: '' };
    }

    public isValidManifestType(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const manifestType = this.utils.getNestedValue(cargo, 'manifestType');
        if (!manifestType || !manifestType.code || !manifestType.description)
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'el tipo de manifiesto') };
        const originMunicipalityCode = this.utils.getNestedValue(cargo, 'cargoFeature.uploadDownload.origin.municipalityCode');
        const destinations: Destination[] = this.utils.getNestedValue(cargo, 'cargoFeature.uploadDownload.destination');
        const hasMultipleCities = destinations && destinations.length && destinations.some(destination => destination && destination.municipalityCode && destination.municipalityCode !== originMunicipalityCode);
        if (!hasMultipleCities && !this.urbanManifestTypes.includes(manifestType.code))
            return { isValid: false, error: 'Por favor seleccione un tipo de manifiesto urbano para servicios dentro del mismo municipio' };
        if (hasMultipleCities && !this.nationalManifestTypes.includes(manifestType.code))
            return { isValid: false, error: 'Por favor seleccione un tipo de manifiesto nacional para servicios entre distintas ciudades' };
        if (manifestType.code === 'D') {
            const manifestDailyTripsCount = this.utils.getNestedValue(cargo, 'manifestDailyTripsCount');
            if (!manifestDailyTripsCount || manifestDailyTripsCount < 1)
                return { isValid: false, error: 'El número de viajes es requerido y debe ser mayor a 0 para el tipo de manifiesto "Varios Viajes en el Día"' };
        }
        return { isValid: true, error: '' };
    }

    public isValidMonitoringCompany(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        if (cargo.state !== CargoStateEnum.REQUEST) return { isValid: true, error: '' };
        const monitoringCompany = this.utils.getNestedValue(cargo, 'monitoringCompany.name');
        if (!monitoringCompany)
            return { isValid: false, error: 'Se requiere volver a guardar la empresa de monitoreo' };
        return { isValid: true, error: '' };
    }

    public isValidConsignments(cargo: Cargo = this.cargo, consignments: ConsignmentCargo[] = this.cargoConsignments): { isValid: boolean, error: string } {
        const tripType = this.utils.getNestedValue(cargo, 'cargoModel.tripType.name');
        if (!consignments || !consignments.length) {
            return {
                isValid: false, error: tripType === TRIP_TYPES.INTERNATIONAL
                    ? 'Para viajes internacionales, debe diligenciar al menos una remesa con la frontera colombiana para generar el manifiesto'
                    : 'Debe diligenciar al menos una remesa'
            };
        }

        const manifestType = this.utils.getNestedValue(cargo, 'manifestType');
        const cargoOwner: ThirdParty = this.utils.getNestedValue(cargo, 'cargoOwner');
        const origin = this.utils.getNestedValue(cargo, 'cargoFeature.uploadDownload.origin');
        const destinations: Destination[] = this.utils.getNestedValue(cargo, 'cargoFeature.uploadDownload.destination');
        const destinationMunicipalityCodes = destinations.map(destination => destination && destination.municipalityCode ? destination.municipalityCode : null);
        const { sender, destination } = this.cargoManager.getSenderAndDestinationConsignmentOptions(cargo);
        const cargoType = this.utils.getNestedValue(cargo, 'cargoModel.cargoType.name');
        const isContainer = cargoType === 'Contenedor';
        const serviceTypeName = this.utils.getNestedValue(cargo, 'cargoModel.serviceType.name');
        const isEmptyContainer = ['Contenedor vacío con carga de compensación', 'Contenedor vacío express'].includes(serviceTypeName);
        const cargoAssociatedConsignments = this.utils.getCargoAssociatedConsignments(cargo);
        if (cargoAssociatedConsignments.length < consignments.length)
            return { isValid: false, error: 'Por favor, verifique la información de cargue y descargue de las remesas' };
        else if (cargoAssociatedConsignments.length > consignments.length)
            return { isValid: false, error: 'Hay inconsistencias en las remesas del servicio. Por favor contacte a soporte' };

        const isSameCityService = this.utils.isSameCity(cargo);
        let hasUrbanConsignments = false;
        let hasNationalConsignments = false;

        for (const [index, consignment] of consignments.entries()) {
            //Validaciones por campos
            for (const field of this.consignmentFieldsToCheck) {
                const value = this.utils.getNestedValue(consignment, field.field);
                if (value !== 0 && !value)
                    return { isValid: false, error: `Se requiere diligenciar el campo ${field.alias} de la remesa ${index + 1}` };
            }
            //Validaciones por campos variables
            const isValidByVariableFields = this.consignmentManager.getCompleteVariableFields(consignment, cargo);
            if (!isValidByVariableFields.valid) return { isValid: false, error: `Error en la remesa ${index + 1}: ${isValidByVariableFields.error}` };
            //Validaciones por campos de mercancías peligrosas
            const isValidByDangerousFields = this.consignmentManager.getCompleteDangerousFields(consignment);
            if (!isValidByDangerousFields.valid) return { isValid: false, error: `Error en la remesa ${index + 1}: ${isValidByDangerousFields.error}` };
            // Errores RNDC no bloquean si la remesa fue autorizada bajo contingencia sin transmitir al ministerio
            const isAuthorizedUnderContingency = consignment.authorizedWithoutConsignment && !consignment.entryId;
            if (consignment.errorCreation && !isAuthorizedUnderContingency) return { isValid: false, error: `Ocurrió un error al generar la remesa ${index + 1}: ${consignment.errorCreation}` };
            if (consignment.errorInitialFulfillment) return { isValid: false, error: `Ocurrió un error al generar la remesa ${index + 1}: ${consignment.errorInitialFulfillment}` };
            if (consignment.errorFinalFulfillment) return { isValid: false, error: `Ocurrió un error al generar la remesa ${index + 1}: ${consignment.errorFinalFulfillment}` };
            if (consignment.errorCancellation) return { isValid: false, error: `Ocurrió un error al generar la remesa ${index + 1}: ${consignment.errorCancellation}` };
            //Validaciones por tipo de manifiesto
            if (manifestType && manifestType.code === 'M' && consignment.recipient.municipalityCode === consignment.sender.municipalityCode)
                return { isValid: false, error: `La remesa ${index + 1} tiene la misma ciudad de origen y destino, esto no es permitido para manifestos multiparada` };
            //Validaciones por propietario de la carga
            if (cargoOwner && (consignment.cargoOwner.documentType !== cargoOwner.documentType || consignment.cargoOwner.documentNumber !== cargoOwner.documentNumber || consignment.cargoOwner.branchOffice !== cargoOwner.branchOffice))
                return { isValid: false, error: `Se requiere volver a guardar al propietario de las remesas` };
            //Validaciones por contenido de las remesas
            if (sender && Object.keys(sender).length) {
                //La ciudad de origen de la remesa debe estar en las ciudades de origen de la carga
                const senderCity = Object.values(sender).find(city => city && city.name === consignment.sender.city && city.index === consignment.sender.cityId);
                if (!senderCity)
                    return { isValid: false, error: `Se requiere volver a seleccionar la ciudad de origen de la remesa ${index + 1}` };
                if (!senderCity || !senderCity.thirdParties || !senderCity.thirdParties.length || senderCity.index !== consignment.sender.cityId || senderCity.countryCode !== 'CO')
                    return { isValid: false, error: `Se requiere volver a seleccionar la ciudad de origen de la remesa ${index + 1}` };
                const senderThirdParty = senderCity.thirdParties.find(thirdParty => thirdParty && thirdParty.documentNumber === consignment.sender.documentNumber &&
                    thirdParty.documentType === consignment.sender.documentType);
                if (!senderThirdParty)
                    return { isValid: false, error: `Se requiere volver a seleccionar el remitente de la remesa ${index + 1}` };
                const senderAddressIndex = senderThirdParty.addresses.findIndex(address => address && address.address && address.address === consignment.sender.address);
                const senderAddress = senderAddressIndex !== -1 ? senderThirdParty.addresses[senderAddressIndex] : null;
                if (!senderAddress || !senderAddress.location || !senderAddress.location.lat || !senderAddress.location.lng)
                    return { isValid: false, error: `Se requiere volver a seleccionar la dirección de origen de la remesa ${index + 1}` };
                if (senderAddress.location.lat !== consignment.sender.location.lat || senderAddress.location.lng !== consignment.sender.location.lng || senderAddress.index !== consignment.sender.addressId)
                    return { isValid: false, error: `Se requiere volver a seleccionar la dirección de origen de la remesa ${index + 1}` };
                const consignmentLoadDate = DateManager.stringToDate(consignment.load.date, 'DD/MM/YYYY ZZ');
                if (consignmentLoadDate && senderCity.date) {
                    const senderLoadDate = DateManager.stringToDate(senderCity.date);
                    if (!DateManager.isSame(consignmentLoadDate, senderLoadDate) && cargo.state === CargoStateEnum.REQUEST)
                        return { isValid: false, error: `Se requiere volver a guardar la información de la remesa ${index + 1}` };
                }
            }
            if (destination && Object.keys(destination).length) {
                //La ciudad de destino de la remesa debe estar en las ciudades de destino de la carga
                const destinationCity = Object.values(destination).find(city => city && city.name === consignment.recipient.city && city.index === consignment.recipient.cityId);
                if (!destinationCity)
                    return { isValid: false, error: `Se requiere volver a seleccionar la ciudad de destino de la remesa ${index + 1}` };
                if (!destinationCity || !destinationCity.thirdParties || !destinationCity.thirdParties.length || destinationCity.index !== consignment.recipient.cityId || destinationCity.countryCode !== 'CO')
                    return { isValid: false, error: `Se requiere volver a seleccionar la ciudad de destino de la remesa ${index + 1}` };
                const destinationThirdParty = destinationCity.thirdParties.find(thirdParty => thirdParty && thirdParty.documentNumber === consignment.recipient.documentNumber &&
                    thirdParty.documentType === consignment.recipient.documentType);
                if (!destinationThirdParty)
                    return { isValid: false, error: `Se requiere volver a seleccionar el destinatario de la remesa ${index + 1}` };
                const destinationAddressIndex = destinationThirdParty.addresses.findIndex(address => address && address.address && address.address === consignment.recipient.address);
                const destinationAddress = destinationAddressIndex !== -1 ? destinationThirdParty.addresses[destinationAddressIndex] : null;
                if (!destinationAddress || !destinationAddress.location || !destinationAddress.location.lat || !destinationAddress.location.lng)
                    return { isValid: false, error: `Se requiere volver a seleccionar la dirección de destino de la remesa ${index + 1}` };
                if (destinationAddress.location.lat !== consignment.recipient.location.lat || destinationAddress.location.lng !== consignment.recipient.location.lng || destinationAddress.index !== consignment.recipient.addressId)
                    return { isValid: false, error: `Se requiere volver a seleccionar la dirección de destino de la remesa ${index + 1}` };
                const consignmentUnloadDate = DateManager.stringToDate(consignment.unload.date, 'DD/MM/YYYY ZZ');
                if (consignmentUnloadDate && destinationCity.date) {
                    const destinationDownloadDate = DateManager.stringToDate(destinationCity.date);
                    if (!DateManager.isSame(consignmentUnloadDate, destinationDownloadDate) && cargo.state === CargoStateEnum.REQUEST)
                        return { isValid: false, error: `Se requiere volver a guardar la información de la remesa ${index + 1}` };
                }
            }
            if (consignment.load.date === consignment.unload.date && consignment.load.appointmentTime === consignment.unload.appointmentTime)
                return { isValid: false, error: `La remesa ${index + 1} tiene la misma fecha y hora de cargue y descargue` };
            const consignmentLoadDate = DateManager.stringToDate(consignment.load.date, 'DD/MM/YYYY ZZ');
            const permissionIgnoreDate = this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.changeCargoLoadDate);
            if (!consignment.entryId && DateManager.isBefore(consignmentLoadDate, new Date()) &&
                [CargoStateEnum.ACCEPTED, CargoStateEnum.CREATED, CargoStateEnum.REQUEST].includes(cargo.state) && !permissionIgnoreDate)
                return { isValid: false, error: `La remesa ${index + 1} tiene una fecha de cargue anterior a la fecha actual` };
            const consignmentUnloadDate = DateManager.stringToDate(consignment.unload.date, 'DD/MM/YYYY ZZ');
            if (!consignment.entryId && DateManager.isBefore(consignmentUnloadDate, new Date()) &&
                [CargoStateEnum.ACCEPTED, CargoStateEnum.CREATED, CargoStateEnum.REQUEST].includes(cargo.state) && !permissionIgnoreDate)
                return { isValid: false, error: `La remesa ${index + 1} tiene una fecha de descargue anterior a la fecha actual` };
            if (DateManager.isBefore(consignmentUnloadDate, consignmentLoadDate))
                return { isValid: false, error: `La remesa ${index + 1} tiene una fecha de descargue anterior a la fecha de cargue` };
            if (DateManager.dateDiff(consignmentUnloadDate, null, consignmentLoadDate, null, 'days') > 5)
                return { isValid: false, error: `La remesa ${index + 1} tiene una fecha de descargue posterior a 5 días a la fecha de cargue` };
            const operationCode = this.utils.getNestedValue(consignment, 'operationType.code');
            if (isContainer && isEmptyContainer && operationCode !== 'V')
                return { isValid: false, error: `La remesa ${index + 1} debe ser de tipo Contenedor vacío acorde al servicio` };
            if (isContainer && !isEmptyContainer && operationCode !== 'C')
                return { isValid: false, error: `La remesa ${index + 1} debe ser de tipo Contenedor cargado acorde al servicio` };
            if (!isContainer && operationCode !== 'G' && operationCode !== 'P')
                return { isValid: false, error: `La remesa ${index + 1} no puede ser de tipo Contenedor` };
            const { duplicated, duplicatedConsignments } = this.cargoManager.verifyDuplicatedConsignments(consignments);
            if (duplicated)
                return { isValid: false, error: `Las remesas ${duplicatedConsignments.map(c => c.id).join(' y ')} son iguales` };
            const senderMunicipalityCode = consignment.sender.municipalityCode;
            const recipientMunicipalityCode = consignment.recipient.municipalityCode;
            if (senderMunicipalityCode === recipientMunicipalityCode)
                hasUrbanConsignments = true;
            else
                hasNationalConsignments = true;
        }
        if (hasUrbanConsignments && hasNationalConsignments)
            return { isValid: false, error: 'No se puede tener remesas urbanas y nacionales en el mismo servicio' };
        if (hasNationalConsignments && isSameCityService)
            return { isValid: false, error: 'No se puede tener remesas nacionales en un servicio que tiene origen y destino en la misma ciudad' };
        if (hasUrbanConsignments && !isSameCityService)
            return { isValid: false, error: 'No se puede tener remesas urbanas en un servicio que tiene origen y destino en distintas ciudades' };


        const everyDestinationHasConsignment = destinationMunicipalityCodes.every(destinationMunicipalityCode => {
            if (!destinationMunicipalityCode) return true;
            if (origin && destinationMunicipalityCode === origin.municipalityCode) return true;
            return consignments.some(consignment => consignment && consignment.recipient && consignment.recipient.municipalityCode === destinationMunicipalityCode);
        });
        if (!everyDestinationHasConsignment) return { isValid: false, error: 'Debe diligenciar al menos una remesa por cada destino distinto al origen' };

        return { isValid: true, error: '' };
    }

    public isValidServicePublication(cargo: Cargo = this.cargo): { isValid: boolean, error: string } {
        const servicePublication = this.utils.getNestedValue(cargo, 'zone');
        if (!servicePublication && this.isTransportService(cargo))
            return { isValid: false, error: this.utils.giveMessageError('MISSING_FIELD_ALTERNATIVE', 'la zona de publicacion') };

        return { isValid: true, error: '' };
    }



    timeFormat(time: string): { time: string, timeType: string } {
        if (!time) return { time: '00:01', timeType: 'AM' };
        if (time[0] === '0' && time[1] !== '0')
            return { time: time.slice(1), timeType: 'AM' };
        else if (time[0] === '1' && time[1] === '2')
            return { time: `00:${time.slice(3, 5)}`, timeType: 'PM' };
        else if ((time[0] === '1' && parseInt(time[1]) > 2) || (time[0] === '2' && parseInt(time[1]) < 2))
            return { time: DateFormatPipe.prototype.transform('01-01-1990 ' + time, 'time').slice(1).slice(0, 4), timeType: 'PM' };
        else if (time[0] === '2')
            return { time: DateFormatPipe.prototype.transform('01-01-1990 ' + time, 'time').slice(0, 5), timeType: 'PM' };
        return { time: time, timeType: 'AM' };
    }

    public async checkTripTimes(cargo: Cargo, validateOrigin = true, validateDestination = true): Promise<{ valid: boolean, error: string }> {
        this.spinner.show();
        let validCargo;
        try { validCargo = await this.cargoManager.checkTripTimes(cargo, validateOrigin, validateDestination).toPromise() }
        catch (error) { console.error(error); }
        this.spinner.hide();
        if (!validCargo)
            return { valid: false, error: 'Ocurrió un error al validar el servicio' };
        if (!validCargo.valid) {
            let message = 'Los tiempos estimados entre las direcciones son imposibles de cumplir.';
            if (validCargo && validCargo.min && validCargo.address) {
                message = `Para llegar a la dirección ${validCargo.address} se requieren al menos `;
                const duration = DateManager.durationFormat(validCargo.min, 'seconds');
                if (duration.hours) message += `${duration.hours} horas `;
                if (duration.minutes) message += `${duration.minutes} minutos `;
                if (duration.seconds) message += `${duration.seconds} segundos `;
            }
            return { valid: false, error: message };
        }
        return { valid: true, error: '' };
    }

    public async getRouteGoogleData(cargo: Cargo): Promise<{ distancy: number, estimatedTime: number }> {
        let success: { cargoDistancy; cargoEstimatedTime } = null;
        try { success = await this.googleService.getRouteDataCargo(cargo) }
        catch (error) { return { distancy: 0, estimatedTime: 0 } }
        return { distancy: success.cargoDistancy, estimatedTime: success.cargoEstimatedTime };
    }

    async updateService(overrideRiskValidation?: boolean): Promise<boolean> {
        return this.updateServiceWithBody(this.getBodyUpdateOnlyRequest(), overrideRiskValidation);
    }

    /**
     * Igual que `updateService`, pero recibe el body ya armado en vez de leerlo del form en el
     * momento de la llamada. Es lo que hay que usar al reintentar un guardado bloqueado por la
     * validacion de riesgo (con `overrideRiskValidation=true`): el primer intento fallido ya hizo
     * `refreshCargo()` en el catch de abajo, que pisa el form con el valor persistido, así que
     * releer el form en el reintento mandaría el valor viejo en vez de lo que el usuario digitó.
     */
    async updateServiceWithBody(body: Cargo, overrideRiskValidation?: boolean): Promise<boolean> {
        return new Promise(async (resolve, reject) => {
            this.spinner.show();
            try { await this.updateOnlyRequest(body, overrideRiskValidation).toPromise() } catch (error) {
                await this.refreshCargo();
                this.spinner.hide();
                return reject(error);
            }
            if (!this.isListeningChangesFromFirebase && this.cargo && this.cargo.consecutive) {
                const response = await this.refreshCargo();
                this.spinner.hide();
                if (response) resolve(true);
                else reject(false);
            } else {
                await this.setCargoConsignments();
                await this.setCargoVehicle();
                this.spinner.hide();
                resolve(true);
            }

        });
    }

    public updateContractorAndTransportCompany(
        cargoId: string,
        body: ChangeTransportCompanyRequest
    ): Observable<ChangeTransportCompanyResponse> {
        return this.http.post<ChangeTransportCompanyResponse>(
            `${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.changeTransportCompany, cargoId)}`,
            body
        );
    }

    public getTransportCompanyChangeJob(jobId: string): Observable<TransportCompanyChangeJob> {
        return this.http.get<TransportCompanyChangeJob>(
            `${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.transportCompanyChangeJob, jobId)}`
        );
    }

    public getBodyUpdateOnlyRequest() {
        const cargo: Cargo = this.utils.clone(this.form.value);
        delete cargo.travelExpenses;
        delete cargo.validatedBankAccount;
        //Update by trip type
        if (!this.isEscortedService(cargo) && !this.isSatelliteLockService(cargo)) delete cargo.escortedVehicle;
        if (!this.isSatelliteLockService(cargo)) delete cargo.lock;
        // El control existe siempre en el formulario, así que sin este delete
        // viajaría transportCompany: null donde el campo no aplica.
        if (!this.requiresTransportCompany(cargo)) { delete cargo.transportCompany; }
        if (!cargo.ministry) delete cargo.monitoringCompany;
        else if (!cargo.monitoringCompany) cargo.monitoringCompany = this.serviceResources.cargoMock.monitoringCompany;
        if (cargo.cargoModel.tripType.name === 'Última milla') {
            cargo.cargoModel.cargoType.name = "Carga suelta";
            const cargoType = this.utils.getNestedValue(cargo, 'cargoModel.cargoType.name');
            const serviceType = this.utils.getNestedValue(cargo, 'cargoModel.serviceType.name');
            const selectedService = this.serviceTypes.find(type => type.name === cargoType);
            if (selectedService && !selectedService.types.includes(serviceType))
                cargo.cargoModel.serviceType.name = "";
        }
        if (!this.isRequiredLetterRetirement(cargo)) cargo.cargoModel.tripType.letterRetirement = null;
        if (!this.isRequiredRegimeType(cargo)) cargo.cargoModel.tripType.regimeType = "";
        if (!this.isRequiredDateVesselArrival(cargo)) cargo.cargoModel.tripType.date = "";
        if (!this.isRequiredContainerSize(cargo)) cargo.cargoModel.cargoType.containerMeasure.size = null;
        if (!this.isContainerService(cargo))
            ['container', 'containers', 'containerExpirationDate', 'booking'].forEach(valueToDelete => cargo[valueToDelete] = null);
        else if (!this.isRequiredContainerInfo(cargo)) delete cargo.container;
        cargo.cargoModel.tripType.category = this.utils.isSameCity(cargo) ? 'Urbana' : 'Nacional';
        //Update by vehicle type
        const vehicleType = this.utils.getNestedValue(cargo, 'cargoFeature.vehicleType.name');
        const bodyWorkType = this.utils.getNestedValue(cargo, 'cargoFeature.vehicleType.bodyWorkType.name');
        if (cargo.licensePlate && cargo.driver) cargo.assignedDriver = true;
        if (!this.cargoManager.requestEnvironmentType(vehicleType, bodyWorkType))
            cargo.cargoFeature.vehicleType.quality = '';
        if (!this.isRequiredTrailer(cargo))
            delete cargo.cargoFeature.vehicleType.trailer;
        if (this.isEmptyContainer(cargo)) {
            cargo.cargoFeature.productType.name = 'CONTENEDOR VACIO';
            cargo.cargoFeature.productType.code = '009990';
        } else {
            cargo.cargoFeature.productType.name = 'Carga Normal';
            cargo.cargoFeature.productType.code = '009880';
        }

        //Update by upload download
        const originInternational = cargo.cargoFeature.uploadDownload.origin.country.code !== 'CO';
        cargo.cargoFeature.uploadDownload.origin.isInternational = originInternational;
        cargo.cargoFeature.uploadDownload.origin.addresses.forEach((address: AddressCargo, i: number) => {
            address.id = i.toString();
            if (this.isEscortedService(cargo) || this.isSatelliteLockService(cargo) || (this.utils.addressHasThirdParty(address) && cargo.ministry && !originInternational)) delete address.thirdPartyConsignment.documentTypeName;
            else delete address.thirdPartyConsignment;
            if (address.idTracking) delete address.idTracking;
            address.address = this.utils.replaceAmpersand(address.address);
            delete address.timeType;
            delete address.timePact;
            delete address.quantity;
            delete address.createGuide;
        });
        cargo.cargoFeature.uploadDownload.destination.forEach((destination: Destination, i: number) => {
            destination.id = i.toString();
            const destinationInternational = destination.country.code !== 'CO';
            destination.isInternational = destinationInternational;
            destination.addresses.forEach((address: AddressCargo, j: number) => {
                address.id = j.toString();
                if (this.isEscortedService(cargo) || this.isSatelliteLockService(cargo) || (this.utils.addressHasThirdParty(address) && cargo.ministry && !destinationInternational)) delete address.thirdPartyConsignment.documentTypeName;
                else delete address.thirdPartyConsignment;
                address.address = this.utils.replaceAmpersand(address.address);
                if (!this.isContainerService(cargo) || this.isRequiredContainerInfo(cargo)) delete address.containers;
                delete address.timeType;
                delete address.timePact;
                delete address.quantity;
                if (address.idTracking) delete address.idTracking;
                if (address.guidesRecipients && address.guidesRecipients.length) {
                    address.numberOfGuides = address.guidesRecipients.length;
                    address.createGuide = true;
                } else {
                    address.numberOfGuides = 0;
                    address.createGuide = false;
                }
                if (address.consignments && address.consignments.length) {
                    if (!cargo.ministry) delete address.consignments;
                    else address.consignments = address.consignments.filter(consignmentId =>
                        this.cargoConsignments.find(consignment => consignment.id === consignmentId));
                }
                delete address.cargoMeasure;
            });
            delete destination.reteica;
        });
        cargo.cargoFeature.uploadDownload['destinationName'] = cargo.cargoFeature.uploadDownload.destination.map(destination => destination.name);

        if (!cargo.ministry) {
            delete cargo.cargoOwner;
            delete cargo.manifestType;
            delete cargo.manifestDailyTripsCount;
        } else {
            delete cargo.cargoOwner.address;
            delete cargo.cargoOwner.location;
            const manifestTypeCode = cargo.manifestType && cargo.manifestType.code;
            if (manifestTypeCode !== 'D')
                delete cargo.manifestDailyTripsCount;
        }
        cargo.holderCompany = this.authService && this.authService.getUserSession() && this.authService.getUserSession().clientCompanyId
            ? this.authService.getUserSession().clientCompanyId
            : null;
        cargo.shippingCost.freightCost = Number(cargo.shippingCost.freightCost);
        if (!cargo.ministry) cargo.shippingCost.skipFreightRNDCReport = false;
        cargo.shippingCost.totalCost = Number(cargo.shippingCost.totalCost);
        cargo.shippingCost.rate = Number(cargo.shippingCost.rate);
        if (cargo.state === CargoStateEnum.REQUEST)
            cargo.shippingCost.initialRate = Number(cargo.shippingCost.rate);
        delete cargo.shippingCost.payments;
        delete cargo.cargoFeature.uploadDownload.origin.isInternational;
        cargo.cargoFeature.uploadDownload.destination.forEach(destination => {
            delete destination.isInternational;
        })
        if (this.isEscortedService(cargo) || this.isSatelliteLockService(cargo)) {
            // CargoFeature
            delete cargo.cargoFeature.cargoMeasure;
            delete cargo.cargoFeature.productType;
            // CargoModel
            delete cargo.cargoModel.cargoNature;
            delete cargo.cargoModel.cargoType;
            delete cargo.cargoModel.operationType;
            delete cargo.cargoModel.packagingType;
        } else {
            delete cargo.cargoFeature.productType.cargoNature;
            delete cargo.cargoFeature.productType.cargoNatureId;
        }
        delete cargo.quantity;
        delete cargo.businessName;
        delete cargo.cargoFeature.uploadDownload.origin.reteica;
        if (!this.isTransportService(cargo)) {
            delete cargo.shippingCost.fopatPercentage;
            delete cargo.shippingCost.fopatValue;
        }

        return cargo;
    }


    public updateOnlyRequest(body: Cargo, overrideRiskValidation?: boolean) {
        let params = new HttpParams();
        if (overrideRiskValidation) params = params.set('overrideRiskValidation', 'true');
        return this.http.put(
            environment.urlServerTeclogi + this.endpointResources.urlUpdateOnlyRequest,
            [body],
            { params }
        )
    }

    async refreshCargo(): Promise<boolean> {
        return new Promise(async (resolve, reject) => {
            let cargo: Cargo;
            try { cargo = await this.cargoDetailService.detailCargoByConsecutive(this.cargo.consecutive.toString()).toPromise() } catch (error) { }
            if (!cargo) return reject(false);
            this.patchForm(cargo, false);
            await this.setCargoConsignments();
            await this.setCargoVehicle();
            resolve(true);
        });
    }

    public applyCargoFromRndcResponse(responseOrError: unknown): boolean {
        const responseCargo = extractCargoFromRndcResponse(responseOrError);
        if (!responseCargo) {
            return false;
        }
        const mergedCargo = mergeCargoFromRndcResponse(this.cargo, responseOrError);
        if (mergedCargo) this.patchForm(mergedCargo, false);
        return true;
    }

    public hasAuthorizedWithoutConsignment(consignments: ConsignmentCargo[] = this.cargoConsignments): boolean {
        return !!(consignments && consignments.some(consignment =>
            consignment && !consignment.entryId && consignment.authorizedWithoutConsignment));
    }

    async setCargoConsignments() {
        if (!this.cargo || !this.cargo.ministry) {
            this.cargoConsignments = [];
            this.consignmentsObservable.next(this.cargoConsignments);
            return;
        }
        let consignments: ConsignmentCargo[] = [];
        try { consignments = await this.cargoDetailService.getCargoConsignments(this.cargo.id, 'Created').toPromise() } catch (error) { }
        this.cargoConsignments = consignments && consignments.length ? consignments : [];
        this.consignmentsObservable.next(this.cargoConsignments);
    }

    async setCargoVehicle() {
        if (!this.cargo || !this.cargo.licensePlate) {
            this.cargoVehicle = null;
            return;
        }
        if (this.cargoVehicle && this.cargoVehicle.id === this.cargo.licensePlate) return;
        let vehicle: Vehicle;
        try { vehicle = await this.vehiclesService.getVehicleFromOwnCompany(this.cargo.licensePlate).toPromise() } catch (error) { }
        this.cargoVehicle = vehicle;
    }
    async updateCargoVehicle(licensePlate: string = this.cargo.licensePlate, overrideRiskValidation?: boolean): Promise<{ canProceed: boolean, driver?: User, error?: string, termsUnaccepted?: string, missingInfo?: { missingDocuments: string[], unacceptedDocuments: string[], invalidReferences: string[] }, riskValidationBlocked?: boolean }> {
        if (!licensePlate || !licensePlate.trim()) return { canProceed: true };
        licensePlate = licensePlate.trim().toUpperCase();
        const vehicleAssignValidationResults = await this.vehicleAssignManager.validateVehiclesForAssign([licensePlate], this.cargo, overrideRiskValidation);
        const { isValid, message, missingInfo, driver, owner, administrator, termsUnaccepted, riskValidationBlocked } = vehicleAssignValidationResults[0];
        if (!isValid) {
            if (termsUnaccepted) return { canProceed: false, driver: driver, termsUnaccepted: termsUnaccepted };
            if (missingInfo) return { canProceed: false, missingInfo: missingInfo };
            if (riskValidationBlocked) return { canProceed: false, error: message, riskValidationBlocked: true };
            return { canProceed: false, error: message };
        }

        const driverDocument = this.utils.getNestedValue(driver, 'information.document');
        const ownerDocument = this.utils.getNestedValue(owner, 'information.document');
        const administratorDocument = this.utils.getNestedValue(administrator, 'information.document');

        if (!this.form.get('licensePlate')) this.form.addControl('licensePlate', new FormControl(licensePlate));
        else this.form.get('licensePlate').setValue(licensePlate);
        if (!this.form.get('driver')) this.form.addControl('driver', new FormControl(driverDocument));
        else this.form.get('driver').setValue(driverDocument);
        if (!this.form.get('owner')) this.form.addControl('owner', new FormControl(ownerDocument ? ownerDocument : null));
        else this.form.get('owner').setValue(ownerDocument ? ownerDocument : null);
        if (!this.form.get('administrator')) this.form.addControl('administrator', new FormControl(administratorDocument ? administratorDocument : null));
        else this.form.get('administrator').setValue(administratorDocument ? administratorDocument : null);
        if (!this.form.get('assignedDriver')) this.form.addControl('assignedDriver', new FormControl(true));
        else this.form.get('assignedDriver').setValue(true);
        const session = this.authService && this.authService.getUserSession ? this.authService.getUserSession() : null;
        if (!this.form.get('assignedVehicleFingerPrint')) this.form.addControl('assignedVehicleFingerPrint', new FormGroup({
            userId: new FormControl(session && session.information && session.information.document ? session.information.document : ''),
            userName: new FormControl(session && session.information && session.information.name ? session.information.name : ''),
            date: new FormControl(DateManager.dateToString(new Date(), 'YYYY-MM-DD HH:mm ZZ'))
        }));

        return { canProceed: true };
    }

    async deleteAllCargoConsignments(): Promise<{ success: boolean, error: string }> {
        if (isDocumentRecreationInProgress(this.cargo))
            return { success: false, error: DOCUMENT_RECREATION_IN_PROGRESS_MESSAGE };
        if (!this.cargoConsignments || !this.cargoConsignments.length) return;
        const promises = [];
        this.cargoConsignments.forEach(consignment => {
            promises.push(this.cargoDetailService.deleteConsignment(consignment.documentId, this.cargo.id).toPromise());
        });
        try { await Promise.all(promises) } catch (error) { return { success: false, error: 'Ocurrió un error al eliminar las remesas' } }
        return { success: true, error: '' };
    }
    async updateAllCargoConsignments(consignments: ConsignmentCargo[]): Promise<{ success: boolean, error: string }> {
        if (isDocumentRecreationInProgress(this.cargo))
            return { success: false, error: DOCUMENT_RECREATION_IN_PROGRESS_MESSAGE };
        try {
            await this.cargoDetailService.updateConsignments(consignments).toPromise();
        } catch (error) { return { success: false, error: 'Ocurrió un error al actualizar las remesas' } }
        return { success: true, error: '' };
    }

    /**
     * @description Gets available label tags
     * @returns Observable with tag list
     */
    public getLabelTags(): Observable<Tag[]> {
        return this.http.get<any>(
            `${environment.urlServerTeclogi}${this.endpointResources.labelTags}`
        ).pipe(
            map(response => response.catalog)
        );
    }

    public isEscortedService(cargo: Cargo = this.cargo): boolean {
        return this.utils.isEscortedService(cargo);
    }
    public isSatelliteLockService(cargo: Cargo = this.cargo): boolean {
        return this.utils.isSatelliteLockService(cargo);
    }
    public isTransportService(cargo: Cargo = this.cargo): boolean {
        return this.utils.isTransportService(cargo);
    }

    public isMinistryService(cargo: Cargo = this.cargo): boolean {
        return !!this.utils.getNestedValue(cargo, 'ministry');
    }

    public isImportOrExport(cargo: Cargo = this.cargo): boolean {
        const tripType = this.utils.getNestedValue(cargo, 'cargoModel.tripType.name');
        if (!tripType) return false;
        return [TRIP_TYPES.EXPORT, TRIP_TYPES.IMPORT].includes(tripType as TRIP_TYPES);
    }

    public isEmptyContainer(cargo: Cargo = this.cargo): boolean {
        const serviceType = this.utils.getNestedValue(cargo, 'cargoModel.serviceType.name');
        return serviceType && serviceType.includes("Contenedor vacío");
    }

    public get utilityCargo() {
        const rate = this.utils.getNestedValue(this.cargo, 'shippingCost.rate') ? this.cargo.shippingCost.rate : 0;
        const totalCost = this.utils.getNestedValue(this.cargo, 'shippingCost.totalCost') ? this.cargo.shippingCost.totalCost : 0;
        if (!rate) return 0;
        return Math.round(((rate - totalCost) / rate) * 100);
    }

    private getTimePactList(): { hours: number, minutes: number, description: string }[] {
        const list = [];
        for (let hours = 0; hours < 24; hours++) {
            for (let minutes = 0; minutes < 60; minutes += 5) {
                if (hours || minutes)
                    list.push({ hours: hours, minutes: minutes, description: `${hours ? hours + ' hr ' : ''}${minutes ? minutes + ' min' : ''}` });
            }
        }
        return list;

    }
}
